<script setup lang="ts">
import { Dialog, Button, InputText, Select, Textarea, MultiSelect, InputNumber, Message, DatePicker, DataTable, Column, Popover, Checkbox, useConfirm } from 'primevue';
import { computed, ref, useTemplateRef, watch, type Ref } from 'vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useCreatePackage } from '../api/createPackage';
import { useUpdatePackage } from '../api/updatePackage';
import { usePackageStore } from 'shared/packages/stores/packageStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import type { PackageContent } from 'shared/packages/domain/packageContent';
import type { Product } from 'shared/products/domain/product';
import { PackageStatus } from 'shared/packages/domain/packageStatus';
import { useProductStore } from 'shared/products/stores/productStore';
import { useMappingTraceStore } from 'shared/products/mapping/stores/mappingTraceStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { isPackageStorage, isPackageOutput } from 'shared/storageAreas/domain/storageAreaType';
import { storagePlaceLabel } from 'shared/storageAreas/domain/computePlaceDisplayText';
import {
  exceedsPlaceMaxHeight,
  exceedsPlaceMaxStackCount,
  exceedsPlaceMaxWeight,
  occupiedPlacesNotContiguous,
  overlapsOtherPackage,
  validatePackagePlacement,
} from './packagePlacementValidation';
import { buildPackageCopy } from './copyPackage';
import {
  applyContentMeasurements,
  contentMeasurements,
  packageComposition,
  packageRunningMeters,
  packageVolume,
  sumContents,
  uniformPackageDimensions,
} from './packageMeasurements';
import { PackageComposition } from 'shared/packages/domain/packageComposition';
import type { Package } from 'shared/packages/domain/package';
import {
  packageDialogGridConfig,
  type PackageDialogGroupId,
} from './packageDialogGridLayout';
import MeasurementInput from '@/common/components/MeasurementInput.vue';
import WeightInput from '@/common/components/WeightInput.vue';
import PanelGridView from '@/common/panelGrid/PanelGridView.vue';
import PanelFieldGrid from '@/common/panelGrid/PanelFieldGrid.vue';
import { usePanelGridDialog } from '@/common/panelGrid/usePanelGridDialog';
import { ProductType } from 'shared/products/domain/productType';
import { formatDuration } from 'shared/common/domain/helperFunctions';
import ColumnSelectorButton from 'shared/common/components/ColumnSelectorButton.vue';
import ColumnFilterCell from 'shared/common/components/ColumnFilterCell.vue';
import { useColumnSelector, type ColumnField } from 'shared/common/composables/useColumnSelector';
import { useColumnFilters } from 'shared/common/composables/useColumnFilters';

const emit = defineEmits<{
  saved: [];
}>();

defineExpose({
  show,
  showCopy,
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const pageTranslations = computed(() => translations.value.packages);

const packageStore = usePackageStore();
const packageTypeStore = usePackageTypeStore();
const productStore = useProductStore();
const mappingTraceStore = useMappingTraceStore();
const storageAreaStore = useStorageAreaStore();
const createPackage = useCreatePackage();
const updatePackage = useUpdatePackage();

const visible = ref(false);
const loading = ref(false);
const serverValidationErrors = ref<string[]>([]);
const packageId = ref<string | null>(null);
const number = ref<string>('');
const externalNumber = ref<string>('');
const status = ref<PackageStatus>(PackageStatus.Active);
const statusValidFrom = ref<Date | null>(null);
const statusValidTo = ref<Date | null>(null);
const packageTypeId = ref<string | null>(null);
const piecesCount = ref<number | null>(null);
const layers = ref<number | null>(null);
const piecesPerLayer = ref<number | null>(null);
const spacerHeight = ref<number | null>(null);
const packageStorageAreaId = ref<string | null>(null);
const occupiedStoragePlaceIds = ref<string[]>([]);
const stackLevel = ref<number | null>(null);
const nominalWeight = ref<number | null>(null);
const weight = ref<number | null>(null);
const nominalHeight = ref<number | null>(null);
const height = ref<number | null>(null);
const nominalWidth = ref<number | null>(null);
const width = ref<number | null>(null);
const nominalLength = ref<number | null>(null);
const length = ref<number | null>(null);
const longitude = ref<number | null>(null);
const latitude = ref<number | null>(null);
const rotation = ref<number | null>(null);
const comment = ref<string>('');
const contents = ref<PackageContent[]>([]);
const selectedContentProductId = ref<string | null>(null);
const contentCodeDialogVisible = ref(false);
const selectedContentIdForCodes = ref<string | null>(null);
const activeContentId = ref<string | null>(null);
const contentSection = useTemplateRef<HTMLElement>('contentSection');

onClickOutside(contentSection, () => {
  if (!contentCodeDialogVisible.value) {
    activeContentId.value = null;
  }
});

// Capture phase so Escape leaves the active row before PrimeVue's Dialog closes on it.
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !visible.value || contentCodeDialogVisible.value || !activeContentId.value) return;
  activeContentId.value = null;
  event.stopPropagation();
}, { capture: true });

const currentPackage = computed(() => (packageId.value ? packageStore.getById(packageId.value) : null));

const ageInDays = computed(() => {
  const createdAt = currentPackage.value?.createdAtUtc;
  if (!createdAt) return null;
  return Math.floor((Date.now() - Date.parse(createdAt)) / 86_400_000);
});

const {
  layoutEditMode,
  gridLayout,
  gridGroups,
  editingSheetId,
  editingSheetName,
  draggedSheetId,
  hoverSheetId,
  selectTabsheet,
  resetLayout,
  moveGroupToNewSheet,
  moveGroupToSheet,
  startRenameTabsheet,
  commitRenameTabsheet,
  onTabsheetDrop,
  panelFieldSettings,
  isFieldVisible,
  setFieldVisible,
  setLabelWidth,
  activePickerPanel,
  dialogStyle,
  dialogContentStyle,
  startResize,
} = usePanelGridDialog<PackageDialogGroupId>({
  config: packageDialogGridConfig,
  layoutStorageKey: 'package-dialog-grid-layout',
  editModeStorageKey: 'package-dialog-layout-edit-mode',
  visible,
});

const confirm = useConfirm();

function confirmResetLayout() {
  confirm.require({
    header: translations.value.generic.resetLayoutConfirmHeader,
    message: translations.value.generic.resetLayoutConfirmMessage,
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: translations.value.generic.abort, severity: 'secondary', outlined: true },
    acceptProps: { label: translations.value.generic.reset, severity: 'warn' },
    accept: () => resetLayout(),
  });
}

const panelFieldDefs = computed<Record<PackageDialogGroupId, { key: string; label: string }[]>>(() => ({
  core: [
    { key: 'number', label: pageTranslations.value.fields.number },
    { key: 'externalNumber', label: pageTranslations.value.fields.externalNumber },
    { key: 'status', label: translations.value.generic.status },
    { key: 'statusValidFrom', label: pageTranslations.value.fields.statusValidFrom },
    { key: 'statusValidTo', label: pageTranslations.value.fields.statusValidTo },
    { key: 'packageType', label: translations.value.sideMenu.packageTypes },
    { key: 'composition', label: pageTranslations.value.fields.composition },
    { key: 'piecesCount', label: pageTranslations.value.fields.piecesCount },
    { key: 'layers', label: pageTranslations.value.fields.layers },
    { key: 'piecesPerLayer', label: pageTranslations.value.fields.piecesPerLayer },
    { key: 'spacerHeight', label: pageTranslations.value.fields.spacerHeight },
    { key: 'packageStorageArea', label: pageTranslations.value.fields.packageStorageArea },
    { key: 'occupiedStoragePlaces', label: pageTranslations.value.fields.occupiedStoragePlaces },
    { key: 'stackLevel', label: pageTranslations.value.fields.stackLevel },
    { key: 'comment', label: pageTranslations.value.fields.comment },
  ],
  physical: [
    { key: 'weight', label: pageTranslations.value.fields.weight },
    { key: 'height', label: pageTranslations.value.fields.height },
    { key: 'width', label: pageTranslations.value.fields.width },
    { key: 'length', label: pageTranslations.value.fields.length },
    { key: 'volume', label: pageTranslations.value.fields.volume },
    { key: 'runningMeters', label: pageTranslations.value.fields.runningMeters },
  ],
  nominal: [
    { key: 'weight', label: pageTranslations.value.fields.weight },
    { key: 'height', label: pageTranslations.value.fields.height },
    { key: 'width', label: pageTranslations.value.fields.width },
    { key: 'length', label: pageTranslations.value.fields.length },
    { key: 'volume', label: pageTranslations.value.fields.volume },
    { key: 'runningMeters', label: pageTranslations.value.fields.runningMeters },
  ],
  contents: [],
  statistics: [
    { key: 'createdAt', label: pageTranslations.value.fields.createdAt },
    { key: 'timesMovedCount', label: pageTranslations.value.fields.timesMovedCount },
    { key: 'distanceTravelledKm', label: pageTranslations.value.fields.distanceTravelledKm },
    { key: 'timeCarried', label: pageTranslations.value.fields.timeCarried },
    { key: 'weightedDistanceTravelledKm', label: pageTranslations.value.fields.weightedDistanceTravelledKm },
  ],
}));

const fieldPicker = useTemplateRef<InstanceType<typeof Popover>>('fieldPicker');

function toggleFieldPicker(panelId: PackageDialogGroupId, event: Event) {
  activePickerPanel.value = panelId;
  fieldPicker.value?.toggle(event);
}

const panelTitles = computed<Record<PackageDialogGroupId, string>>(() => ({
  core: pageTranslations.value.groups.core,
  physical: pageTranslations.value.groups.physical,
  nominal: pageTranslations.value.groups.nominal,
  contents: 'Contents',
  statistics: pageTranslations.value.groups.statistics,
}));

const packageTypeOptions = computed(() => {
  return packageTypeStore.sortedByName.map((item) => ({ label: item.name, value: item.id }));
});

const productOptions = computed(() => {
    return productStore.sortedByName
    .filter((item) => item.type == ProductType.Dimension)
    .map((item) => ({ label: item.name, value: item.id }));
});

interface ContentColumn extends ColumnField {
  hint?: string;
  defaultWidth?: string;
}

const contentTableTranslations = computed(() => pageTranslations.value.contentTable);

const mappingTraceFilterOptions = computed(() => {
  const ids = [...new Set(contents.value.map((content) => content.mappingTraceId).filter((id): id is string => !!id))];
  return ids.map((id) => ({ label: mappingTraceLabel(id), value: id }));
});

const contentColumns = computed<ContentColumn[]>(() => {
  const details = pageTranslations.value.details;
  const hints = contentTableTranslations.value.hints;
  return [
    { field: 'productId', header: details.product, filterType: 'select', filterOptions: productOptions.value, defaultWidth: '10rem' },
    { field: 'pieces', header: details.pieces, filterType: 'numeric', defaultWidth: '5rem' },
    { field: 'moisture', header: details.moisture, filterType: 'numeric', hint: hints.moisture, defaultWidth: '6rem' },
    { field: 'quality', header: details.quality, filterType: 'text', hint: hints.quality, defaultWidth: '6rem' },
    { field: 'partNumber', header: details.partNumber, filterType: 'text', hint: hints.partNumber, defaultWidth: '7rem' },
    {
      field: 'mappingTraceId',
      header: contentTableTranslations.value.mappingTrace,
      filterType: 'select',
      filterOptions: mappingTraceFilterOptions.value,
      hint: hints.mappingTrace,
      defaultWidth: '10rem',
    },
    { field: 'physical', header: pageTranslations.value.groups.physical, hint: hints.physical, defaultWidth: '15rem' },
    { field: 'nominal', header: pageTranslations.value.groups.nominal, hint: hints.nominal, defaultWidth: '15rem' },
    { field: 'codes', header: details.codes, hint: hints.codes, defaultWidth: '12rem' },
  ];
});

const contentDimensionFields = [
  { physical: 'weight', nominal: 'nominalWeight', label: 'weight', placeholder: 'W', derived: null },
  { physical: 'thickness', nominal: 'nominalThickness', label: 'thickness', placeholder: 'T', derived: null },
  { physical: 'width', nominal: 'nominalWidth', label: 'width', placeholder: 'Wd', derived: null },
  { physical: 'length', nominal: 'nominalLength', label: 'length', placeholder: 'L', derived: null },
  { physical: 'volume', nominal: 'nominalVolume', label: 'volume', placeholder: 'V', derived: 'volume' },
  { physical: 'runningMeters', nominal: 'nominalRunningMeters', label: 'runningMeters', placeholder: 'Rm', derived: 'runningMeters' },
] as const;

type ContentDimensionField = (typeof contentDimensionFields)[number];

function dimensionKind(field: string): 'physical' | 'nominal' {
  return field === 'nominal' ? 'nominal' : 'physical';
}

function isDerivedDimension(content: PackageContent, dimension: ContentDimensionField, field: string): boolean {
  return dimension.derived !== null && contentMeasurements(content)[dimensionKind(field)][dimension.derived] !== null;
}

function formatContentValue(value: number | null, maximumFractionDigits = 3): string {
  if (value === null) return '-';
  return new Intl.NumberFormat(languageStore.language.code, { maximumFractionDigits }).format(value);
}

function contentRowClass(content: PackageContent) {
  return content.id === activeContentId.value ? 'is-active-row' : undefined;
}

const {
  orderedColumns: orderedContentColumns,
  selectedColumns: selectedContentColumns,
  columnOrder: contentColumnOrder,
  isVisible: isContentColumnVisible,
  setSelectedColumns: setSelectedContentColumns,
  setOrder: setContentColumnOrder,
  reset: resetContentColumns,
} = useColumnSelector({
  storageKey: 'package-content-table-columns',
  columns: contentColumns,
});

const {
  filters: contentFilters,
  filteredCount: filteredContentCount,
  hasActiveFilters: hasActiveContentFilters,
  clearFilters: clearContentFilters,
  onFilter: onContentFilter,
} = useColumnFilters({
  columns: contentColumns,
  totalCount: () => contents.value.length,
});

const contentTableVersion = ref(0);

// Remount on order changes: PrimeVue's internal column order would otherwise override the v-for order.
const contentTableKey = computed(() =>
  `${selectedContentColumns.value.map((column) => column.field).join(',')}|${contentTableVersion.value}`,
);

function onContentColumnReorder(event: { dragIndex: number; dropIndex: number }) {
  const visible = selectedContentColumns.value.map((column) => column.field);
  const [moved] = visible.splice(event.dragIndex, 1);
  if (moved) {
    visible.splice(event.dropIndex, 0, moved);
    let next = 0;
    contentColumnOrder.value = orderedContentColumns.value.map((column) =>
      isContentColumnVisible(column.field) ? visible[next++]! : column.field,
    );
  }
  contentTableVersion.value++;
}

const contentColumnWidthsStorageKey = 'package-content-table-column-widths';

function loadContentColumnWidths(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(contentColumnWidthsStorageKey) ?? '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return Object.fromEntries(
        Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0),
      );
    }
  } catch {
    // Corrupt persisted widths fall back to the defaults.
  }
  return {};
}

// Keyed by field (not position like PrimeVue's stateStorage) so widths survive hiding and reordering columns.
const contentColumnWidths = ref<Record<string, number>>(loadContentColumnWidths());

function contentColumnStyle(column: ContentColumn) {
  const persisted = contentColumnWidths.value[column.field];
  const width = persisted ? `${persisted}px` : column.defaultWidth;
  return width ? { width } : undefined;
}

function onContentColumnResizeEnd(event: { element: HTMLElement }) {
  const headerCells = Array.from(event.element.parentElement?.children ?? []);
  const field = selectedContentColumns.value[headerCells.indexOf(event.element)]?.field;
  if (!field) return;
  contentColumnWidths.value = { ...contentColumnWidths.value, [field]: event.element.offsetWidth };
  localStorage.setItem(contentColumnWidthsStorageKey, JSON.stringify(contentColumnWidths.value));
}

function resetContentTable() {
  resetContentColumns();
  contentColumnWidths.value = {};
  localStorage.removeItem(contentColumnWidthsStorageKey);
  contentTableVersion.value++;
}

function toNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getNumberField(entity: object, fieldName: string): number | null {
  const value = (entity as Record<string, unknown>)[fieldName];
  return toNumberOrNull(value);
}

function applyProductDefaults(content: PackageContent, product: Product | null) {
  if (!product) {
    return;
  }

  content.quality = product.quality;

  // Dimension fields live on the product's nested dimensionData, not the product itself.
  const dimension = product.dimensionData;
  if (!dimension) {
    return;
  }

  content.thickness = getNumberField(dimension, 'thickness') ?? content.thickness;
  content.width = getNumberField(dimension, 'width') ?? content.width;
  content.length = getNumberField(dimension, 'length') ?? content.length;
  content.moisture = getNumberField(dimension, 'moisture') ?? content.moisture;

  content.nominalThickness = getNumberField(dimension, 'nominalThickness') ?? content.nominalThickness;
  content.nominalWidth = getNumberField(dimension, 'nominalWidth') ?? content.nominalWidth;
  content.nominalLength = getNumberField(dimension, 'nominalLength') ?? content.nominalLength;
}

function createEmptyContent(order: number): PackageContent {
  return {
    id: crypto.randomUUID(),
    productId: null,
    mappingTraceId: null,
    order,
    pieces: null,
    nominalWeight: null,
    weight: null,
    nominalThickness: null,
    thickness: null,
    nominalWidth: null,
    width: null,
    nominalLength: null,
    length: null,
    nominalVolume: null,
    volume: null,
    nominalRunningMeters: null,
    runningMeters: null,
    moisture: null,
    quality: null,
    partNumber: null,
    codes: [],
  };
}

function addContentRow() {
  const productId = selectedContentProductId.value;
  if (!productId) return;

  clearContentFilters();
  const row = createEmptyContent(contents.value.length);
  row.productId = productId;
  applyProductDefaults(row, productStore.getById(productId) ?? null);

  contents.value.push(row);
  activeContentId.value = row.id;
}

function removeContentRow(contentId: string) {
  contents.value = contents.value.filter((item) => item.id !== contentId);
  if (activeContentId.value === contentId) {
    activeContentId.value = null;
  }
}

function updateContentProduct(content: PackageContent, productId: string | null) {
  content.productId = productId;
  applyProductDefaults(content, productId ? productStore.getById(productId) ?? null : null);
}

function mappingTraceLabel(id: string | null): string {
  if (!id) return '-';
  const trace = mappingTraceStore.getById(id);
  return trace?.resolvedProductName ?? trace?.sourceProductName ?? id;
}

function openContentCodes(contentId: string) {
  selectedContentIdForCodes.value = contentId;
  contentCodeDialogVisible.value = true;
}

const selectedContentCodes = computed(() => {
  const content = contents.value.find((item) => item.id === selectedContentIdForCodes.value);
  return content?.codes ?? [];
});

function addContentCode() {
  selectedContentCodes.value.push({
    id: crypto.randomUUID(),
    code: '',
    codeType: '',
  });
}

function removeContentCode(codeId: string) {
  const content = contents.value.find((item) => item.id === selectedContentIdForCodes.value);
  if (!content) {
    return;
  }

  content.codes = content.codes.filter((code) => code.id !== codeId);
}

const storageAreaOptions = computed(() => {
  return storageAreaStore.storageAreas
    .filter((area) => isPackageStorage(area.type) || isPackageOutput(area.type))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((area) => ({
      label: area.name,
      value: area.id,
    }));
});

function areaPlaces(area: { packageStorageData?: { places: { id: string; index: number; row: number | null; column: number | null; disabled: boolean; displayText: string | null }[] } | null; packagePlaceData?: { places: { id: string; index: number; row: number | null; column: number | null; disabled: boolean; displayText: string | null }[] } | null } | undefined) {
  return area?.packageStorageData?.places ?? area?.packagePlaceData?.places ?? [];
}

const selectedStorageArea = computed(() => {
  if (!packageStorageAreaId.value) {
    return null;
  }

  return storageAreaStore.getById(packageStorageAreaId.value);
});

const placeOptions = computed(() => {
  const places = areaPlaces(selectedStorageArea.value ?? undefined);

  return places
    .filter((place) => !place.disabled)
    .sort((a, b) => a.index - b.index)
    .map((place) => ({
      label: storagePlaceLabel(place),
      value: place.id,
    }));
});

const placeById = computed(() => {
  const places = areaPlaces(selectedStorageArea.value ?? undefined);
  return new Map(places.map((place) => [place.id, { index: place.index, row: place.row, column: place.column }]));
});

const validationErrors = computed(() => {
  const placementErrors = validatePackagePlacement({
    packageStorageAreaId: packageStorageAreaId.value,
    occupiedStoragePlaceIds: occupiedStoragePlaceIds.value,
    stackLevel: stackLevel.value,
    placeById: placeById.value,
  });

  const negativeChecks: [number | null, string][] = [
    [nominalWeight.value, 'Weight cannot be negative.'],
    [weight.value, 'Weight cannot be negative.'],
    [nominalHeight.value, 'Height cannot be negative.'],
    [height.value, 'Height cannot be negative.'],
    [nominalWidth.value, 'Width cannot be negative.'],
    [width.value, 'Width cannot be negative.'],
    [nominalLength.value, 'Length cannot be negative.'],
    [length.value, 'Length cannot be negative.'],
  ];

  const dimensionErrors = new Set<string>();
  for (const [value, message] of negativeChecks) {
    if (value !== null && value < 0) {
      dimensionErrors.add(message);
    }
  }

  return [...placementErrors, ...dimensionErrors];
});

const displayedValidationErrors = computed(() => {
  return [...new Set([...validationErrors.value, ...serverValidationErrors.value])];
});

const placementWarnings = computed(() => {
  const area = selectedStorageArea.value;
  const areaData = area?.packageStorageData ?? area?.packagePlaceData;
  if (!area || !areaData) {
    return [];
  }

  const otherPackages = packageStore.packages.filter((pkg) => pkg.packageStorageAreaId === area.id && pkg.id !== packageId.value);
  const occupiedIds = occupiedStoragePlaceIds.value;
  const warnings: string[] = [];

  if (occupiedPlacesNotContiguous(occupiedIds, placeById.value)) {
    warnings.push(pageTranslations.value.placesNotContiguousWarning);
  }

  if (exceedsPlaceMaxStackCount({
    stackLevel: stackLevel.value,
    occupiedStoragePlaceIds: occupiedIds,
    places: areaData.places,
    defaultMaxStackCount: areaData.defaultMaxStackCount,
  })) {
    warnings.push(pageTranslations.value.stackLevelExceedsMaxWarning);
  }

  if (overlapsOtherPackage({ stackLevel: stackLevel.value, occupiedStoragePlaceIds: occupiedIds, otherPackages })) {
    warnings.push(pageTranslations.value.placementOverlapWarning);
  }

  if (exceedsPlaceMaxWeight({
    weight: weight.value,
    occupiedStoragePlaceIds: occupiedIds,
    places: areaData.places,
    defaultMaxWeight: areaData.defaultMaxWeight,
    otherPackages,
  })) {
    warnings.push(pageTranslations.value.weightExceedsMaxWarning);
  }

  if (exceedsPlaceMaxHeight({
    height: height.value,
    occupiedStoragePlaceIds: occupiedIds,
    places: areaData.places,
    defaultMaxHeight: areaData.defaultMaxHeight,
    otherPackages,
  })) {
    warnings.push(pageTranslations.value.heightExceedsMaxWarning);
  }

  return warnings;
});

watch(packageStorageAreaId, () => {
  const validPlaceIds = new Set(placeOptions.value.map((o) => o.value));

  occupiedStoragePlaceIds.value = occupiedStoragePlaceIds.value.filter((id) => validPlaceIds.has(id));
  serverValidationErrors.value = [];
});

watch([occupiedStoragePlaceIds, stackLevel], () => {
  serverValidationErrors.value = [];
});

watch([nominalWeight, weight, nominalHeight, height, nominalWidth, width, nominalLength, length], () => {
  serverValidationErrors.value = [];
});

watch(contents, (items) => items.forEach(applyContentMeasurements), { deep: true });

const composition = computed(() => packageComposition(contents.value));

function onPackageTypeSelected(id: string | null) {
  packageTypeId.value = id;
  const packageType = id ? packageTypeStore.getById(id) : null;
  if (!packageType) return;

  piecesCount.value = packageType.pieces;
  layers.value = packageType.layers;
  piecesPerLayer.value = packageType.piecesPerLayer;
  spacerHeight.value = packageType.spacerHeight;
}

type DimensionKey = 'height' | 'width' | 'length' | 'nominalHeight' | 'nominalWidth' | 'nominalLength';

// Last value written by the uniform calculation per field; a differing value means the user edited it.
const autoFilledDimensions: Record<DimensionKey, number | null> = {
  height: null,
  width: null,
  length: null,
  nominalHeight: null,
  nominalWidth: null,
  nominalLength: null,
};

function resetAutoFilledDimensions() {
  for (const key of Object.keys(autoFilledDimensions) as DimensionKey[]) {
    autoFilledDimensions[key] = null;
  }
}

function fillDimension(target: Ref<number | null>, key: DimensionKey, value: number | null) {
  if (target.value !== null && target.value !== autoFilledDimensions[key]) return;
  target.value = value;
  autoFilledDimensions[key] = value;
}

watch([contents, layers, piecesPerLayer, spacerHeight], () => {
  const uniform = composition.value === PackageComposition.Uniform;
  const layering = { layers: layers.value, piecesPerLayer: piecesPerLayer.value, spacerHeight: spacerHeight.value };
  const empty = { height: null, width: null, length: null };
  const physical = uniform ? uniformPackageDimensions(contents.value, layering, 'physical') : empty;
  const nominal = uniform ? uniformPackageDimensions(contents.value, layering, 'nominal') : empty;

  fillDimension(height, 'height', physical.height);
  fillDimension(width, 'width', physical.width);
  fillDimension(length, 'length', physical.length);
  fillDimension(nominalHeight, 'nominalHeight', nominal.height);
  fillDimension(nominalWidth, 'nominalWidth', nominal.width);
  fillDimension(nominalLength, 'nominalLength', nominal.length);
}, { deep: true });

// With contents only their sums count; otherwise derive from the package totals like the backend.
const hasContents = computed(() => contents.value.length > 0);
const displayedNominalVolume = computed(() => hasContents.value
  ? sumContents(contents.value, 'nominalVolume')
  : packageVolume(nominalHeight.value, nominalWidth.value, nominalLength.value));
const displayedVolume = computed(() => hasContents.value
  ? sumContents(contents.value, 'volume')
  : packageVolume(height.value, width.value, length.value));
const displayedNominalRunningMeters = computed(() => hasContents.value
  ? sumContents(contents.value, 'nominalRunningMeters')
  : packageRunningMeters(piecesCount.value, nominalLength.value));
const displayedRunningMeters = computed(() => hasContents.value
  ? sumContents(contents.value, 'runningMeters')
  : packageRunningMeters(piecesCount.value, length.value));

const statusOptions = [
  { label: 'Active', value: PackageStatus.Active },
  { label: 'Deleted', value: PackageStatus.Deleted },
  { label: 'Reserved', value: PackageStatus.Reserved },
  { label: 'Locked', value: PackageStatus.Locked },
];

const title = computed(() => {
  return packageId.value ? pageTranslations.value.editPackage : pageTranslations.value.addNewPackage;
});

function show(id?: string, preset?: { packageStorageAreaId?: string; placeId?: string }) {
  serverValidationErrors.value = [];
  resetAutoFilledDimensions();

  if (!id) {
    packageId.value = null;
    number.value = '';
    externalNumber.value = '';
    status.value = PackageStatus.Active;
    statusValidFrom.value = null;
    statusValidTo.value = null;
    packageTypeId.value = null;
    piecesCount.value = null;
    layers.value = null;
    piecesPerLayer.value = null;
    spacerHeight.value = null;
    packageStorageAreaId.value = preset?.packageStorageAreaId ?? null;
    occupiedStoragePlaceIds.value = preset?.placeId ? [preset.placeId] : [];
    stackLevel.value = null;
    nominalWeight.value = null;
    weight.value = null;
    nominalHeight.value = null;
    height.value = null;
    nominalWidth.value = null;
    width.value = null;
    nominalLength.value = null;
    length.value = null;
    longitude.value = null;
    latitude.value = null;
    rotation.value = null;
    comment.value = '';
    contents.value = [];
    clearContentFilters();
    activeContentId.value = null;
    selectedContentProductId.value = null;
    selectedContentIdForCodes.value = null;
    contentCodeDialogVisible.value = false;
    visible.value = true;
    return;
  }

  const item = packageStore.getById(id);
  if (!item) {
    return;
  }

  packageId.value = item.id;
  applyPackage(item);
}

function showCopy(id: string) {
  serverValidationErrors.value = [];
  resetAutoFilledDimensions();

  const item = packageStore.getById(id);
  if (!item) {
    return;
  }

  packageId.value = null;
  applyPackage(buildPackageCopy(item, () => crypto.randomUUID()));
}

function applyPackage(item: Package) {
  number.value = item.number ?? '';
  externalNumber.value = item.externalNumber ?? '';
  status.value = item.status;
  statusValidFrom.value = item.statusValidFrom ? new Date(item.statusValidFrom) : null;
  statusValidTo.value = item.statusValidTo ? new Date(item.statusValidTo) : null;
  packageTypeId.value = item.packageTypeId;
  piecesCount.value = item.piecesCount;
  layers.value = item.layers;
  piecesPerLayer.value = item.piecesPerLayer;
  spacerHeight.value = item.spacerHeight;
  packageStorageAreaId.value = item.packageStorageAreaId;
  occupiedStoragePlaceIds.value = [...item.occupiedStoragePlaceIds];
  stackLevel.value = item.stackLevel;
  nominalWeight.value = item.nominalWeight;
  weight.value = item.weight;
  nominalHeight.value = item.nominalHeight;
  height.value = item.height;
  nominalWidth.value = item.nominalWidth;
  width.value = item.width;
  nominalLength.value = item.nominalLength;
  length.value = item.length;
  longitude.value = item.longitude;
  latitude.value = item.latitude;
  rotation.value = item.rotation;
  comment.value = item.comment ?? '';
  contents.value = (item.contents ?? []).map((content) => ({
    ...content,
    codes: content.codes.map((code) => ({ ...code })),
  }));
  clearContentFilters();
  activeContentId.value = null;
  selectedContentProductId.value = null;
  selectedContentIdForCodes.value = null;
  contentCodeDialogVisible.value = false;
  visible.value = true;
}

async function save() {
  const trimmedNumber = number.value.trim();
  if (!trimmedNumber || validationErrors.value.length > 0) {
    return;
  }

  loading.value = true;
  try {
    const normalizedContents = contents.value.map((content, index) => ({
      ...content,
      order: index,
      codes: content.codes.map((code) => ({ ...code })),
    }));

    contents.value = normalizedContents;

    const payload = {
      number: trimmedNumber,
      externalNumber: externalNumber.value || null,
      status: status.value,
      statusValidFrom: statusValidFrom.value ? statusValidFrom.value.toISOString() : null,
      statusValidTo: statusValidTo.value ? statusValidTo.value.toISOString() : null,
      packageTypeId: packageTypeId.value,
      piecesCount: piecesCount.value,
      layers: layers.value,
      piecesPerLayer: piecesPerLayer.value,
      spacerHeight: spacerHeight.value,
      packageStorageAreaId: packageStorageAreaId.value,
      occupiedStoragePlaceIds: occupiedStoragePlaceIds.value,
      stackLevel: stackLevel.value,
      nominalWeight: nominalWeight.value,
      weight: weight.value,
      nominalHeight: nominalHeight.value,
      height: height.value,
      nominalWidth: nominalWidth.value,
      width: width.value,
      nominalLength: nominalLength.value,
      length: length.value,
      longitude: longitude.value,
      latitude: latitude.value,
      rotation: rotation.value,
      comment: comment.value || null,
      contents: normalizedContents,
    };

    const response = packageId.value
      ? await updatePackage.execute({ id: packageId.value, entity: payload })
      : await createPackage.execute({ entity: payload });

    if (!response.ok) {
      serverValidationErrors.value = toServerValidationMessages(response.details);
      return;
    }

    serverValidationErrors.value = [];
    visible.value = false;
    emit('saved');
  } finally {
    loading.value = false;
  }
}

function toServerValidationMessages(details: { title: string; detail?: string; errors?: { [key: string]: string[] } }): string[] {
  const messages: string[] = [];

  if (details.detail) {
    messages.push(details.detail);
  }

  if (details.errors) {
    for (const values of Object.values(details.errors)) {
      messages.push(...values);
    }
  }

  if (messages.length === 0) {
    messages.push('Failed to save package due to validation errors.');
  }

  return [...new Set(messages)];
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="title" :style="dialogStyle" :contentStyle="dialogContentStyle" :draggable="true" class="package-edit-dialog">
    <div class="package-dialog-body">
      <div class="dialog-top-controls">
        <Button
          v-if="layoutEditMode"
          text
          rounded
          icon="pi pi-undo"
          class="customization-toggle-button"
          :aria-label="translations.generic.reset"
          @click="confirmResetLayout"
        ></Button>
        <span class="layout-mode-label">{{ pageTranslations.layoutMode }}</span>
        <Button
          text
          rounded
          :icon="layoutEditMode ? 'pi pi-times' : 'pi pi-sliders-h'"
          class="customization-toggle-button"
          :aria-label="layoutEditMode ? pageTranslations.hideDialogOptions : pageTranslations.showDialogOptions"
          @click="layoutEditMode = !layoutEditMode"
        ></Button>
      </div>

      <Message v-for="error in displayedValidationErrors" :key="error" severity="warn" size="small" variant="simple" class="validation-message">
        {{ error }}
      </Message>
      <Message v-for="warning in placementWarnings" :key="warning" severity="warn" size="small" variant="simple" icon="pi pi-exclamation-triangle" class="validation-message">
        {{ warning }}
      </Message>

      <div v-if="gridLayout.tabsheets.length > 1" class="tabsheet-bar">
        <div
          v-for="sheet in gridLayout.tabsheets"
          :key="sheet.id"
          class="tabsheet"
          :class="{ 'is-active': sheet.id === gridLayout.activeTabsheetId, 'is-drop-target': hoverSheetId === sheet.id }"
          :data-sheet-id="sheet.id"
          :draggable="layoutEditMode && editingSheetId !== sheet.id"
          @click="selectTabsheet(sheet.id)"
          @dragstart="draggedSheetId = sheet.id"
          @dragover.prevent
          @drop="onTabsheetDrop(sheet.id)"
        >
          <InputText
            v-if="editingSheetId === sheet.id"
            v-model="editingSheetName"
            class="tabsheet-input"
            @click.stop
            @keyup.enter="commitRenameTabsheet"
            @blur="commitRenameTabsheet"
          ></InputText>
          <template v-else>
            <span class="tabsheet-name">{{ sheet.name }}</span>
            <i
              v-if="layoutEditMode"
              class="pi pi-pencil tabsheet-edit"
              title="Rename tabsheet"
              @click.stop="startRenameTabsheet(sheet)"
            ></i>
          </template>
        </div>
      </div>

      <div class="package-dialog-workspace">
        <PanelGridView
          v-model="gridGroups"
          :config="packageDialogGridConfig"
          :panel-titles="panelTitles"
          :layout-edit-mode="layoutEditMode"
          @move-to-new-sheet="moveGroupToNewSheet"
          @move-to-sheet="moveGroupToSheet"
          @sheet-hover="hoverSheetId = $event"
        >
          <template #actions-core>
            <Button
              v-if="layoutEditMode"
              text
              rounded
              icon="pi pi-list"
              class="field-picker-button"
              aria-label="Toggle fields"
              @click="toggleFieldPicker('core', $event)"
            ></Button>
          </template>
          <template #panel-core>
            <PanelFieldGrid
              :fields="panelFieldDefs.core"
              :hidden="panelFieldSettings('core').hidden"
              :label-width="panelFieldSettings('core').labelWidth"
              :layout-edit-mode="layoutEditMode"
              @update:label-width="setLabelWidth('core', $event)"
            >
              <template #number><InputText v-model="number"></InputText></template>
              <template #externalNumber><InputText v-model="externalNumber"></InputText></template>
              <template #status>
                <Select v-model="status" :options="statusOptions" optionLabel="label" optionValue="value"></Select>
              </template>
              <template #statusValidFrom>
                <DatePicker v-model="statusValidFrom" :date-format="languageStore.getPrimeVueDateFormat()" show-time hour-format="24"
                  show-seconds show-icon icon-display="button" show-button-bar fluid></DatePicker>
              </template>
              <template #statusValidTo>
                <DatePicker v-model="statusValidTo" :date-format="languageStore.getPrimeVueDateFormat()" show-time hour-format="24"
                  show-seconds show-icon icon-display="button" show-button-bar fluid></DatePicker>
              </template>
              <template #packageType>
                <Select :modelValue="packageTypeId" :options="packageTypeOptions" optionLabel="label" optionValue="value" showClear
                  @update:modelValue="onPackageTypeSelected"></Select>
              </template>
              <template #composition>
                <InputText :modelValue="pageTranslations.compositions[composition]" disabled></InputText>
              </template>
              <template #piecesCount><InputNumber v-model="piecesCount" :min="0"></InputNumber></template>
              <template #layers><InputNumber v-model="layers" :min="0"></InputNumber></template>
              <template #piecesPerLayer><InputNumber v-model="piecesPerLayer" :min="0"></InputNumber></template>
              <template #spacerHeight>
                <MeasurementInput v-model="spacerHeight" unitKey="package-spacer-height" :min="0"></MeasurementInput>
              </template>
              <template #packageStorageArea>
                <Select v-model="packageStorageAreaId" :options="storageAreaOptions" optionLabel="label" optionValue="value" showClear></Select>
              </template>
              <template #occupiedStoragePlaces>
                <MultiSelect v-model="occupiedStoragePlaceIds" :options="placeOptions" optionLabel="label" optionValue="value"
                  display="chip" :disabled="!packageStorageAreaId"></MultiSelect>
              </template>
              <template #stackLevel>
                <InputNumber v-model="stackLevel" :min="0" :disabled="!packageStorageAreaId"></InputNumber>
              </template>
              <template #comment><Textarea v-model="comment" rows="4"></Textarea></template>
            </PanelFieldGrid>
          </template>

          <template #actions-physical>
            <Button
              v-if="layoutEditMode"
              text
              rounded
              icon="pi pi-list"
              class="field-picker-button"
              aria-label="Toggle fields"
              @click="toggleFieldPicker('physical', $event)"
            ></Button>
          </template>
          <template #panel-physical>
            <PanelFieldGrid
              :fields="panelFieldDefs.physical"
              :hidden="panelFieldSettings('physical').hidden"
              :label-width="panelFieldSettings('physical').labelWidth"
              :layout-edit-mode="layoutEditMode"
              @update:label-width="setLabelWidth('physical', $event)"
            >
              <template #weight><WeightInput v-model="weight" unitKey="package-weight" :min="0"></WeightInput></template>
              <template #height><MeasurementInput v-model="height" unitKey="package-height" :min="0"></MeasurementInput></template>
              <template #width><MeasurementInput v-model="width" unitKey="package-width" :min="0"></MeasurementInput></template>
              <template #length><MeasurementInput v-model="length" unitKey="package-length" :min="0"></MeasurementInput></template>
              <template #volume><InputNumber :modelValue="displayedVolume" :disabled="true" :maxFractionDigits="3" suffix=" m³"></InputNumber></template>
              <template #runningMeters><InputNumber :modelValue="displayedRunningMeters" :disabled="true" :maxFractionDigits="3" suffix=" m"></InputNumber></template>
            </PanelFieldGrid>
          </template>

          <template #actions-nominal>
            <Button
              v-if="layoutEditMode"
              text
              rounded
              icon="pi pi-list"
              class="field-picker-button"
              aria-label="Toggle fields"
              @click="toggleFieldPicker('nominal', $event)"
            ></Button>
          </template>
          <template #panel-nominal>
            <PanelFieldGrid
              :fields="panelFieldDefs.nominal"
              :hidden="panelFieldSettings('nominal').hidden"
              :label-width="panelFieldSettings('nominal').labelWidth"
              :layout-edit-mode="layoutEditMode"
              @update:label-width="setLabelWidth('nominal', $event)"
            >
              <template #weight><WeightInput v-model="nominalWeight" unitKey="package-nominal-weight" :min="0"></WeightInput></template>
              <template #height><MeasurementInput v-model="nominalHeight" unitKey="package-nominal-height" :min="0"></MeasurementInput></template>
              <template #width><MeasurementInput v-model="nominalWidth" unitKey="package-nominal-width" :min="0"></MeasurementInput></template>
              <template #length><MeasurementInput v-model="nominalLength" unitKey="package-nominal-length" :min="0"></MeasurementInput></template>
              <template #volume><InputNumber :modelValue="displayedNominalVolume" :disabled="true" :maxFractionDigits="3" suffix=" m³"></InputNumber></template>
              <template #runningMeters><InputNumber :modelValue="displayedNominalRunningMeters" :disabled="true" :maxFractionDigits="3" suffix=" m"></InputNumber></template>
            </PanelFieldGrid>
          </template>

          <template #panel-contents>
            <div ref="contentSection" class="content-section">
            <div class="content-toolbar">
              <Select
                v-model="selectedContentProductId"
                :options="productOptions"
                optionLabel="label"
                optionValue="value"
                filter
                showClear
                placeholder="Product"
              ></Select>
              <Button icon="pi pi-plus" :label="translations.generic.add" :disabled="!selectedContentProductId" @click="addContentRow"></Button>
              <span v-if="hasActiveContentFilters && filteredContentCount < contents.length" class="filtered-count">
                ({{ translations.dataTable.showing }} {{ filteredContentCount }} {{ translations.dataTable.of }} {{ contents.length }})
              </span>
              <Button v-if="hasActiveContentFilters" type="button" severity="secondary" size="small" icon="pi pi-filter-slash"
                :label="translations.dataTable.clearFilters" @click="clearContentFilters"></Button>
            </div>

            <DataTable
              :key="contentTableKey"
              v-model:filters="contentFilters"
              :value="contents"
              class="content-table"
              size="small"
              filterDisplay="menu"
              reorderableColumns
              resizableColumns
              columnResizeMode="expand"
              :rowClass="contentRowClass"
              @row-click="activeContentId = $event.data.id"
              @filter="onContentFilter"
              @column-reorder="onContentColumnReorder"
              @column-resize-end="onContentColumnResizeEnd"
            >
              <Column
                v-for="col of selectedContentColumns"
                :key="col.field"
                :columnKey="col.field"
                :style="contentColumnStyle(col)"
                :field="col.filterType ? col.field : undefined"
                :filter="!!col.filterType"
                :filterField="col.filterField ?? col.field"
                :showFilterMatchModes="col.showFilterMatchModes"
                :showFilterOperator="col.showFilterOperator"
                :dataType="col.filterType === 'numeric' ? 'numeric' : undefined"
              >
                <template #header>
                  <span v-if="col.hint" v-tooltip.top="col.hint" class="content-column-header">
                    {{ col.header }}
                    <i class="pi pi-info-circle content-column-hint"></i>
                  </span>
                  <span v-else class="content-column-header">{{ col.header }}</span>
                </template>
                <template #body="{ data }">
                  <template v-if="col.field === 'productId'">
                    {{ productStore.getById(data.productId)?.name ?? '-' }}
                  </template>
                  <template v-else-if="col.field === 'pieces'">
                    <InputNumber v-if="data.id === activeContentId" v-model="data.pieces" :min="0" size="small" fluid></InputNumber>
                    <span v-else class="content-number">{{ formatContentValue(data.pieces, 0) }}</span>
                  </template>
                  <template v-else-if="col.field === 'moisture'">
                    <InputNumber v-if="data.id === activeContentId" v-model="data.moisture" :maxFractionDigits="3" size="small" fluid></InputNumber>
                    <span v-else class="content-number">{{ formatContentValue(data.moisture) }}</span>
                  </template>
                  <template v-else-if="col.field === 'quality'">
                    <InputText v-if="data.id === activeContentId" v-model="data.quality" size="small" fluid></InputText>
                    <span v-else>{{ data.quality || '-' }}</span>
                  </template>
                  <template v-else-if="col.field === 'partNumber'">
                    <InputText v-if="data.id === activeContentId" v-model="data.partNumber" size="small" fluid></InputText>
                    <span v-else>{{ data.partNumber || '-' }}</span>
                  </template>
                  <template v-else-if="col.field === 'mappingTraceId'">
                    <router-link v-if="data.mappingTraceId" :to="{ name: 'mappingTrace' }">
                      {{ mappingTraceLabel(data.mappingTraceId) }}
                    </router-link>
                    <span v-else>-</span>
                  </template>
                  <div v-else-if="col.field === 'physical' || col.field === 'nominal'" class="content-dimension-grid">
                    <template v-for="dimension in contentDimensionFields" :key="dimension.label">
                      <InputNumber
                        v-if="data.id === activeContentId"
                        v-model="data[dimension[dimensionKind(col.field)]]"
                        v-tooltip.top="contentTableTranslations.inputs[dimension.label]"
                        :ariaLabel="contentTableTranslations.inputs[dimension.label]"
                        :maxFractionDigits="3"
                        :placeholder="dimension.placeholder"
                        :disabled="isDerivedDimension(data, dimension, col.field)"
                        size="small"
                      ></InputNumber>
                      <span v-else v-tooltip.top="contentTableTranslations.inputs[dimension.label]" class="content-number">
                        {{ formatContentValue(data[dimension[dimensionKind(col.field)]]) }}
                      </span>
                    </template>
                  </div>
                  <button v-else-if="col.field === 'codes'" type="button" class="content-codes" :aria-label="col.header" @click="openContentCodes(data.id)">
                    <span v-for="code in data.codes" :key="code.id" class="content-code-chip">
                      {{ code.code || '-' }}
                      <span v-if="code.codeType" class="content-code-type">{{ code.codeType }}</span>
                    </span>
                    <span v-if="data.codes.length === 0" class="content-empty">-</span>
                  </button>
                </template>
                <template v-if="col.field === 'productId'" #editor="{ data }">
                  <Select
                    :modelValue="data.productId"
                    :options="productOptions"
                    optionLabel="label"
                    optionValue="value"
                    filter
                    showClear
                    @update:modelValue="updateContentProduct(data, $event ?? null)"
                  ></Select>
                </template>
                <template v-if="col.filterType" #filter="{ filterModel, filterCallback }">
                  <ColumnFilterCell :filter-type="col.filterType" :filter-model="filterModel" :filter-callback="filterCallback"
                    :options="col.filterOptions"
                    :placeholder="translations.dataTable.filterBy + ' ' + col.header.toLocaleLowerCase()" />
                </template>
              </Column>

              <Column column-key="actions" :reorderableColumn="false" class="w-12 !text-end">
                <template #header>
                  <ColumnSelectorButton :columns="orderedContentColumns" :modelValue="selectedContentColumns"
                    @update:modelValue="setSelectedContentColumns" @reorder="setContentColumnOrder" @reset="resetContentTable" />
                </template>
                <template #body="{ data }">
                  <Button text severity="danger" size="small" icon="pi pi-trash" @click.stop="removeContentRow(data.id)"></Button>
                </template>
              </Column>
            </DataTable>
            </div>
          </template>

          <template #actions-statistics>
            <Button
              v-if="layoutEditMode"
              text
              rounded
              icon="pi pi-list"
              class="field-picker-button"
              aria-label="Toggle fields"
              @click="toggleFieldPicker('statistics', $event)"
            ></Button>
          </template>
          <template #panel-statistics>
            <PanelFieldGrid
              :fields="panelFieldDefs.statistics"
              :hidden="panelFieldSettings('statistics').hidden"
              :label-width="panelFieldSettings('statistics').labelWidth"
              :layout-edit-mode="layoutEditMode"
              @update:label-width="setLabelWidth('statistics', $event)"
            >
              <template #createdAt>
                <span class="stat-value">
                  {{ currentPackage?.createdAtUtc ? languageStore.formatDateTime(currentPackage.createdAtUtc) : '—' }}
                  <span v-if="ageInDays !== null" class="stat-age">({{ languageStore.formatNumber(ageInDays, 0) }} d)</span>
                </span>
              </template>
              <template #timesMovedCount>
                <span class="stat-value">{{ currentPackage ? languageStore.formatNumber(currentPackage.timesMovedCount, 0) : '—' }}</span>
              </template>
              <template #distanceTravelledKm>
                <span class="stat-value">{{ currentPackage ? `${languageStore.formatNumber(currentPackage.distanceTravelledMetres / 1000, 3)} km` : '—' }}</span>
              </template>
              <template #timeCarried>
                <span class="stat-value">{{ currentPackage ? formatDuration(currentPackage.timeCarriedSeconds) : '—' }}</span>
              </template>
              <template #weightedDistanceTravelledKm>
                <span class="stat-value">{{ currentPackage ? `${languageStore.formatNumber(currentPackage.weightedDistanceTravelledMetres / 1000, 3)} km` : '—' }}</span>
              </template>
            </PanelFieldGrid>
          </template>
        </PanelGridView>

        <Popover ref="fieldPicker">
          <div class="flex flex-col gap-2 min-w-48 max-h-80 overflow-auto">
            <div v-for="field in panelFieldDefs[activePickerPanel]" :key="field.key" class="flex items-center gap-2">
              <Checkbox
                :inputId="`fp-${activePickerPanel}-${field.key}`"
                :modelValue="isFieldVisible(activePickerPanel, field.key)"
                binary
                @update:modelValue="setFieldVisible(activePickerPanel, field.key, $event)"
              ></Checkbox>
              <label :for="`fp-${activePickerPanel}-${field.key}`" class="cursor-pointer">{{ field.label }}</label>
            </div>
          </div>
        </Popover>
      </div>
    </div>

    <template #footer>
      <Button :label="translations.generic.cancel" text @click="visible = false"></Button>
      <Button
        :label="translations.generic.save"
        :loading="loading"
        :severity="placementWarnings.length > 0 ? 'warn' : undefined"
        :icon="placementWarnings.length > 0 ? 'pi pi-exclamation-triangle' : undefined"
        :disabled="validationErrors.length > 0 || !number.trim()"
        @click="save"
      ></Button>
      <div v-if="layoutEditMode" class="resize-handle" @mousedown.prevent="startResize"></div>
    </template>
  </Dialog>

  <Dialog v-model:visible="contentCodeDialogVisible" modal header="Content Codes" :style="{ width: '680px' }">
    <div class="content-code-toolbar">
      <Button icon="pi pi-plus" :label="translations.generic.add" @click="addContentCode"></Button>
    </div>
    <DataTable :value="selectedContentCodes" size="small">
      <Column field="code" header="Code">
        <template #body="{ data }">
          <InputText v-model="data.code"></InputText>
        </template>
      </Column>
      <Column field="codeType" header="Code Type">
        <template #body="{ data }">
          <InputText v-model="data.codeType"></InputText>
        </template>
      </Column>
      <Column header="">
        <template #body="{ data }">
          <Button text severity="danger" icon="pi pi-trash" @click="removeContentCode(data.id)"></Button>
        </template>
      </Column>
    </DataTable>
    <template #footer>
      <Button :label="translations.generic.close" text @click="contentCodeDialogVisible = false"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.package-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
  min-height: 0;
}

.tabsheet-bar {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

.tabsheet {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--p-content-border-color);
  border-bottom: none;
  border-radius: 0.4rem 0.4rem 0 0;
  background: color-mix(in srgb, var(--p-content-background) 92%, var(--p-primary-color) 8%);
  color: var(--p-text-color);
  font-size: 0.82rem;
  cursor: pointer;
  user-select: none;
}

.tabsheet.is-active {
  background: color-mix(in srgb, var(--p-content-background) 74%, var(--p-primary-color) 26%);
  font-weight: 600;
}

.tabsheet.is-drop-target {
  border-color: var(--p-primary-color);
  box-shadow: inset 0 0 0 1px var(--p-primary-color);
}

.tabsheet-edit {
  font-size: 0.72rem;
  opacity: 0.6;
}

.tabsheet-edit:hover {
  opacity: 1;
  color: var(--p-primary-color);
}

.tabsheet-input {
  width: 8rem;
  height: 1.5rem;
  padding: 0 0.35rem;
  font-size: 0.82rem;
}

.dialog-top-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.layout-mode-label {
  color: var(--p-text-muted-color);
  font-size: 0.82rem;
}

.customization-toggle-button {
  flex: 0 0 auto;
}

.package-dialog-workspace {
  flex: 1;
  min-height: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: minmax(8rem, 12rem) minmax(0, 1fr);
  gap: 0.75rem;
  align-items: center;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  height: 100%;
}

.content-toolbar,
.content-code-toolbar {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.5rem;
  align-items: center;
}

.content-toolbar {
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  grid-auto-columns: auto;
}

.filtered-count {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.content-column-header {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.content-column-hint {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  cursor: help;
}

.content-table {
  width: 100%;
}

.content-section :deep(.p-datatable) {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
}

:deep(.content-table .p-datatable-table-container) {
  overflow: auto;
  flex: 1;
}

.content-table :deep(.p-datatable-thead > tr > th),
.content-table :deep(.p-datatable-tbody > tr > td) {
  padding: 0.2rem 0.4rem;
  font-size: 0.85rem;
}

.content-table :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.content-table :deep(.p-datatable-tbody > tr.is-active-row) {
  background: color-mix(in srgb, var(--p-content-background) 85%, var(--p-primary-color) 15%);
  cursor: default;
}

.content-number {
  font-variant-numeric: tabular-nums;
}

.content-empty {
  color: var(--p-text-muted-color);
}

.content-codes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
  width: 100%;
  min-height: 1.4rem;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
  cursor: pointer;
}

.content-code-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  padding: 0 0.35rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 0.3rem;
  background: color-mix(in srgb, var(--p-content-background) 90%, var(--p-primary-color) 10%);
  font-size: 0.78rem;
  line-height: 1.3rem;
}

.content-code-type {
  color: var(--p-text-muted-color);
  font-size: 0.72rem;
}

.content-dimension-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.2rem 0.5rem;
}

.content-dimension-grid :deep(.p-inputnumber),
.content-dimension-grid :deep(.p-inputnumber-input) {
  width: 100%;
  min-width: 0;
}

.validation-message {
  margin-bottom: 0.35rem;
}

.stat-value {
  font-variant-numeric: tabular-nums;
}

.stat-age {
  opacity: 0.65;
  font-size: 0.875em;
  margin-left: 0.35rem;
}

.resize-handle {
  margin-left: auto;
  width: 0.85rem;
  height: 0.85rem;
  cursor: nwse-resize;
  border-right: 2px solid var(--p-text-muted-color);
  border-bottom: 2px solid var(--p-text-muted-color);
}

.field-picker-button.p-button {
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
}

.field-picker-button :deep(.p-button-icon) {
  font-size: 0.82rem;
}
</style>
